import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Radio, Typography, Divider } from 'antd';
import { cn } from '@/libs/utils';
import FunctionaryOwnershipDrawer from './FunctionaryOwnershipDrawer';

const { Title } = Typography;

/**
 * Functionary and Ownership Form Component (SPNFT 1, 2, 3)
 *
 * Displays radio buttons to select document type:
 * - Client Questionnaire - SPNFT 1
 * - Statement of Functionary - SPNFT 2
 * - Statement of Ownership - SPNFT 3
 *
 * Opens a drawer when a selection is made.
 *
 * @param {string} companyId - Company ID
 * @param {Object} companyData - Company data including name and ownershipStructure
 */
export default function FunctionaryOwnershipForm({
  companyId,
  companyData,
  // Profile: no card of its own and no title (the panel shows them).
  bare = false,
}) {
  const { t } = useTranslation(['forms']);
  const [form] = Form.useForm();
  const [selectedValue, setSelectedValue] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const options = [
    { label: t('functionary_ownership.questionnaire', { ns: 'forms' }), value: 'questionnarie' },
    { label: t('functionary_ownership.functionary', { ns: 'forms' }), value: 'functionary' },
    { label: t('functionary_ownership.ownership', { ns: 'forms' }), value: 'ownership' },
  ];

  const onChange = (e) => {
    setSelectedValue(e.target.value);
    form.setFieldsValue({ documentType: e.target.value });
    setDrawerOpen(true);
  };

  const onClose = () => {
    setDrawerOpen(false);
    form.setFieldsValue({ documentType: null });
    setSelectedValue(null);
  };

  return (
    <div
      className={cn(
        !bare && 'mx-5 my-5 max-w-3xl rounded-lg bg-white p-5 shadow-md'
      )}
    >
      {!bare && (
        <>
          <Title level={3} className="text-left">
            {t('functionary_ownership.title', { ns: 'forms' })}
          </Title>
          <Divider style={{ backgroundColor: '#2db1ab50', margin: '20px 0' }} />
        </>
      )}

      <Form form={form} layout="vertical">
        <Form.Item
          name="documentType"
          label={t('functionary_ownership.document_type', { ns: 'forms' })}
          rules={[
            {
              required: true,
              message: t('formValidation.required', { ns: 'forms' }),
            },
          ]}
        >
          <Radio.Group
            onChange={onChange}
            value={selectedValue}
            className="flex flex-col text-left"
          >
            {options.map((option) => (
              <Radio key={option.value} value={option.value} className="my-1">
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
        <Divider style={{ backgroundColor: '#2db1ab50', margin: '20px 0' }} />
      </Form>

      <FunctionaryOwnershipDrawer
        open={drawerOpen}
        onClose={onClose}
        type={selectedValue}
        companyId={companyId}
        companyData={companyData}
      />
    </div>
  );
}
